import { appConfig } from "../app/config/appConfig";
import { postJson } from "./apiClient";

const severityMap = {
  error: "error",
  fail: "error",
  critical: "error",
  warning: "warning",
  warn: "warning",
  info: "info",
  note: "info",
};

const getFindingLine = (finding) =>
  finding?.line ??
  finding?.line_number ??
  finding?.location?.line ??
  finding?.location?.startLine ??
  finding?.range?.start?.line ??
  null;

const normalizeSeverity = (value) => severityMap[String(value || "").toLowerCase()] || "info";

const normalizeFinding = (finding, index) => {
  const severity = normalizeSeverity(
    finding?.severity || finding?.level || finding?.result || finding?.type || finding?.status
  );
  const line = getFindingLine(finding);
  const references = Array.isArray(finding?.references)
    ? finding.references
        .map((reference, referenceIndex) => {
          const title = String(reference?.title || "").trim();
          const link = String(reference?.link || "").trim();

          if (!title && !link) {
            return null;
          }

          return {
            id: `${finding?.id || finding?.ruleId || finding?.code || finding?.category || index + 1}-reference-${referenceIndex + 1}`,
            title: title || "Reference",
            link,
          };
        })
        .filter(Boolean)
    : [];
  const description = String(
    finding?.message ||
      finding?.description ||
      finding?.summary ||
      finding?.why ||
      "No description provided."
  );
  const suggestedFix = String(
      finding?.suggestedFix ||
      finding?.fix ||
      finding?.recommendation ||
      finding?.remediation ||
      ""
  );

  return {
    id: String(
      finding?.id ||
        finding?.ruleId ||
        finding?.code ||
        finding?.key ||
        finding?.category ||
        `${severity}-${index + 1}`
    ),
    severity,
    title: String(
      finding?.ruleId ||
        finding?.code ||
        finding?.title ||
        finding?.key ||
        finding?.category ||
        `${severity.toUpperCase()}_${index + 1}`
    ),
    description,
    suggestedFix,
    references,
    line,
  };
};

const getCategoryFindings = (response) => {
  if (!Array.isArray(response?.categories)) {
    return [];
  }

  return response.categories.flatMap((category) => {
    let items = [];

    if (Array.isArray(category?.errors)) {
      items = category.errors;
    } else if (Array.isArray(category?.findings)) {
      items = category.findings;
    } else if (Array.isArray(category?.issues)) {
      items = category.issues;
    }

    return items.map((finding) => ({
      ...finding,
      category: category?.category,
    }));
  });
};

const getFindingsArray = (response) => {
  if (Array.isArray(response?.findings)) {
    return response.findings;
  }

  if (Array.isArray(response?.issues)) {
    return response.issues;
  }

  if (Array.isArray(response?.results)) {
    return response.results;
  }

  const categoryFindings = getCategoryFindings(response);

  if (categoryFindings.length) {
    return categoryFindings;
  }

  return [];
};

const buildSeverityCounts = (findings) =>
  findings.reduce(
    (accumulator, finding) => ({
      ...accumulator,
      [finding.severity]: accumulator[finding.severity] + 1,
    }),
    { error: 0, warning: 0, info: 0 }
  );

const buildSummary = (findings) => {
  const severityCounts = buildSeverityCounts(findings);

  return {
    total: findings.length,
    errors: severityCounts.error,
    warnings: severityCounts.warning,
    info: severityCounts.info,
  };
};

const buildBackendSummary = (summary = {}) => {
  return {
    total:
      summary.total ??
      summary.total_issues ??
      summary.count ??
      summary.findingCount ??
      0,
    errors: summary.errors ?? summary.errorCount ?? 0,
    warnings: summary.warnings ?? summary.warningCount ?? 0,
    info: summary.info ?? summary.infos ?? summary.infoCount ?? 0,
  };
};

export const normalizeSpecReviewResponse = (response) => {
  const findings = getFindingsArray(response).map(normalizeFinding);
  const summaryFromFindings = buildSummary(findings);
  const backendSummary = buildBackendSummary(response?.summary);

  return {
    findings,
    summary: summaryFromFindings,
    backendSummary,
  };
};

export const reviewSpec = async ({ openapi, source }) => {
  const response = await postJson(appConfig.specReviewUrl, {
    openapi,
    source,
  });

  return normalizeSpecReviewResponse(response);
};
